/**
 * Application-level Error Boundary.
 *
 * Last-line-of-defence so a render error in any one page (e.g. a null
 * field that wasn't expected, an array passed to message.error, a JSX
 * crash from an upstream API change) doesn't unmount the whole app
 * and leave the user staring at a blank white screen.
 *
 * Catches the error, logs to console, and renders a friendly panel
 * with a "Reload page" button so the user can recover without
 * closing the tab. Resets when the route changes.
 */
import { Component, type ReactNode } from 'react'
import { Alert, Button, Result, Space } from 'antd'
import { ReloadOutlined, BugOutlined } from '@ant-design/icons'

interface State {
  hasError: boolean
  error: Error | null
  // Reset when the location changes — set by the wrapper.
  pathSnapshot: string
}

interface Props {
  children: ReactNode
  /** Pass `location.pathname` so the boundary auto-clears on route change. */
  pathname?: string
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, pathSnapshot: props.pathname || '' }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Surface to console so devs can grab the stack from devtools
    // without losing it behind the friendly fallback.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Caught:', error, info)
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.pathname !== this.props.pathname && this.state.hasError) {
      this.setState({ hasError: false, error: null, pathSnapshot: this.props.pathname || '' })
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children
    const message = this.state.error?.message || 'An unexpected error occurred while rendering this page.'
    return (
      <Result
        status="warning"
        icon={<BugOutlined />}
        title="Something went wrong on this screen"
        subTitle="The rest of the app is still working — pick another menu item, or try again."
        extra={
          <Space direction="vertical" style={{ width: '100%', maxWidth: 700 }}>
            <Alert
              type="error" showIcon
              message="Render error"
              description={message}
            />
            <Button
              type="primary" icon={<ReloadOutlined />}
              onClick={() => window.location.reload()}
            >
              Reload page
            </Button>
          </Space>
        }
      />
    )
  }
}
